import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  REQUEST_STATUS,
  getAllCreationRequests,
  observeCreationRequest,
  updateRequestStatus,
  addPreview,
  removePreview,
  setFinalPreviewUrl,
  addStaffMessage,
  sendStaffMessageEmail,
  sendFeedbackRequest,
  addDomainSuggestion,
  removeDomainSuggestion
} from '../services/creationRequestService';

const REQUESTS_KEY = 'creationRequests';

/**
 * Liste des demandes de création assistée
 * @param {string|null} statusFilter - Filtrer par statut
 * @param {string} stateFilter - '' | 'closedByClient' (clôturées par le client)
 */
export const useCreationRequests = (statusFilter, stateFilter) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [REQUESTS_KEY],
    queryFn: () => getAllCreationRequests(),
    select: (result) => {
      const requests = result?.requests || [];
      let filtered = requests;
      if (statusFilter) {
        filtered = filtered.filter((r) => r.status === statusFilter);
      }
      if (stateFilter === 'closedByClient') {
        // Demande clôturée par le client (hors « Site publié » posé par l'admin)
        filtered = filtered.filter(
          (r) =>
            r.trackingClosed === true &&
            r.status !== REQUEST_STATUS.SITE_PUBLIE &&
            (r.closedBy === 'client' || !r.closedBy)
        );
      }
      return filtered;
    }
  });

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: [REQUESTS_KEY] });

  return { requests: data, isLoading, isError, error, refetch, invalidateList };
};

/**
 * Détail + actions d'une demande (statut, previews, messages, email)
 * @param {string|null} userId - ID de l'utilisateur de la demande
 */
export const useCreationRequestDetail = (userId) => {
  const queryClient = useQueryClient();

  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  // Observation temps réel de la demande (messages, statut, previews, …)
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setError(null);
    const unsubscribe = observeCreationRequest(userId, (data, err) => {
      if (cancelled) return;
      if (err) {
        setIsError(true);
        setError(err);
        setIsLoading(false);
        return;
      }
      setRequest(data);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);

  // Le détail est temps réel (onSnapshot) → on invalide uniquement la liste
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [REQUESTS_KEY] });
  };

  const updateStatusMutation = useMutation({
    mutationFn: (status) => updateRequestStatus(userId, status),
    onSuccess: invalidate
  });

  const addPreviewMutation = useMutation({
    mutationFn: (preview) => addPreview(userId, preview),
    onSuccess: invalidate
  });

  const removePreviewMutation = useMutation({
    mutationFn: (index) => removePreview(userId, index),
    onSuccess: invalidate
  });

  const setFinalPreviewMutation = useMutation({
    mutationFn: (url) => setFinalPreviewUrl(userId, url),
    onSuccess: invalidate
  });

  const sendMessageMutation = useMutation({
    mutationFn: ({ content, staffName }) => addStaffMessage(userId, content, staffName),
    onSuccess: invalidate
  });

  const sendStaffEmailMutation = useMutation({
    mutationFn: () => sendStaffMessageEmail(userId),
    onSuccess: invalidate
  });

  const sendFeedbackMutation = useMutation({
    mutationFn: () => sendFeedbackRequest(userId),
    onSuccess: invalidate
  });

  const addDomainSuggestionMutation = useMutation({
    mutationFn: (suggestion) => addDomainSuggestion(userId, suggestion),
    onSuccess: invalidate
  });

  const removeDomainSuggestionMutation = useMutation({
    mutationFn: (index) => removeDomainSuggestion(userId, index),
    onSuccess: invalidate
  });

  return {
    request,
    isLoading,
    isError,
    error,
    updateStatus: updateStatusMutation.mutate,
    isUpdatingStatus: updateStatusMutation.isPending,
    addPreview: addPreviewMutation.mutate,
    isAddingPreview: addPreviewMutation.isPending,
    removePreview: removePreviewMutation.mutate,
    setFinalPreviewUrl: setFinalPreviewMutation.mutate,
    addDomainSuggestion: addDomainSuggestionMutation.mutate,
    removeDomainSuggestion: removeDomainSuggestionMutation.mutate,
    isManagingSuggestions: addDomainSuggestionMutation.isPending || removeDomainSuggestionMutation.isPending,
    sendMessage: sendMessageMutation.mutate,
    isSendingMessage: sendMessageMutation.isPending,
    sendStaffEmail: sendStaffEmailMutation.mutate,
    isSendingStaffEmail: sendStaffEmailMutation.isPending,
    sendFeedback: sendFeedbackMutation.mutate,
    isSendingFeedback: sendFeedbackMutation.isPending
  };
};
